/**
 * Integrations Overview Page
 * 
 * Main dashboard for managing all external service integrations
 * including Jobber CRM, Google My Business, Analytics, and WordPress Migration
 */

import React, { useState } from 'react'
import { Plus, Search, CheckCircle, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useIntegrations } from '@/features/integrations/hooks/useIntegrations'
import { integrationUtils } from '@/features/integrations/utils/integrationUtils'
import { IntegrationType, IntegrationStatus, IntegrationConfig } from '@/types/integrations'
import { IntegrationCard } from '@/features/integrations/components/IntegrationCard'
import { CreateIntegrationDialog } from '@/features/integrations/components/CreateIntegrationDialog'
import { IntegrationStatsCards } from '@/features/integrations/components/IntegrationStatsCards'
import { QuickSetupGuide } from '@/features/integrations/components/QuickSetupGuide'

export default function IntegrationsOverview() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<IntegrationStatus | 'all'>('all')
  const [typeFilter, setTypeFilter] = useState<IntegrationType | 'all'>('all')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [selectedType, setSelectedType] = useState<IntegrationType | null>(null)

  const { integrations, isLoading, refetch } = useIntegrations()

  // Filter integrations based on search and filters
  const filteredIntegrations = integrations.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         integrationUtils.typeLabels[integration.type].toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || integration.status === statusFilter
    const matchesType = typeFilter === 'all' || integration.type === typeFilter

    return matchesSearch && matchesStatus && matchesType
  })

  // Group integrations by type for better organization
  const integrationsByType = filteredIntegrations.reduce((acc, integration) => {
    if (!acc[integration.type]) {
      acc[integration.type] = []
    }
    acc[integration.type].push(integration)
    return acc
  }, {} as Record<IntegrationType, IntegrationConfig[]>)

  // Calculate statistics
  const stats = {
    total: integrations.length,
    connected: integrations.filter(i => i.status === 'connected').length,
    errors: integrations.filter(i => i.status === 'error').length,
    syncing: integrations.filter(i => i.status === 'syncing').length,
  }

  const handleCreateIntegration = (type: IntegrationType) => {
    setSelectedType(type)
    setShowCreateDialog(true)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Integrations</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-muted rounded w-1/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Integrations</h1>
          <p className="text-muted-foreground mt-1">
            Connect and manage external services to streamline your business operations
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Integration
        </Button>
      </div>

      {/* Statistics Cards */}
      <IntegrationStatsCards stats={stats} />

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search integrations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as IntegrationStatus | 'all')}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="disconnected">Disconnected</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="syncing">Syncing</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as IntegrationType | 'all')}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="jobber_crm">Jobber CRM</SelectItem>
                <SelectItem value="google_my_business">Google My Business</SelectItem>
                <SelectItem value="google_analytics">Google Analytics</SelectItem>
                <SelectItem value="google_search_console">Search Console</SelectItem>
                <SelectItem value="facebook_pixel">Facebook Pixel</SelectItem>
                <SelectItem value="wordpress">WordPress</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Integration Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="crm">CRM & Customer Data</TabsTrigger>
          <TabsTrigger value="marketing">Marketing & Analytics</TabsTrigger>
          <TabsTrigger value="content">Content & Migration</TabsTrigger>
          <TabsTrigger value="setup">Quick Setup</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {filteredIntegrations.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No integrations found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Get started by adding your first integration'}
                </p>
                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' ? (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchTerm('')
                      setStatusFilter('all')
                      setTypeFilter('all')
                    }}
                  >
                    Clear Filters
                  </Button>
                ) : (
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Integration
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredIntegrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  onRefresh={refetch}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="crm" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">CRM & Customer Data Integrations</h3>
              <div className="space-y-4">
                {integrationsByType.jobber_crm?.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onRefresh={refetch}
                    variant="detailed"
                  />
                ))}
                {!integrationsByType.jobber_crm?.length && (
                  <Card>
                    <CardContent className="p-6">
                      <div className="text-center">
                        <h4 className="font-semibold mb-2">Connect Jobber CRM</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Sync customer data, jobs, and invoices with your Jobber account
                        </p>
                        <Button onClick={() => handleCreateIntegration('jobber_crm')}>
                          Connect Jobber
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Features</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-chart-5" />
                  Customer synchronization
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-chart-5" />
                  Job management
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-chart-5" />
                  Invoice tracking
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-chart-5" />
                  Estimate management
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-chart-5" />
                  Lead auto-creation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-chart-5" />
                  Two-way data sync
                </li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="marketing" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Marketing & Analytics Integrations</h3>
              <div className="space-y-4">
                {[
                  ...integrationsByType.google_my_business || [],
                  ...integrationsByType.google_analytics || [],
                  ...integrationsByType.google_search_console || [],
                  ...integrationsByType.facebook_pixel || [],
                ].map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onRefresh={refetch}
                    variant="detailed"
                  />
                ))}
                {![
                  'google_my_business',
                  'google_analytics', 
                  'google_search_console',
                  'facebook_pixel'
                ].some(type => integrationsByType[type as IntegrationType]?.length) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="p-6">
                        <div className="text-center">
                          <h4 className="font-semibold mb-2">Google My Business</h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            Manage listings, reviews, and posts
                          </p>
                          <Button onClick={() => handleCreateIntegration('google_my_business')}>
                            Connect GMB
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-6">
                        <div className="text-center">
                          <h4 className="font-semibold mb-2">Google Analytics</h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            Track website traffic and conversions
                          </p>
                          <Button onClick={() => handleCreateIntegration('google_analytics')}>
                            Connect Analytics
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Marketing Features</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Multi-location management
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Review monitoring
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Traffic analytics
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Conversion tracking
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  SEO monitoring
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Campaign optimization
                </li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Content & Migration Tools</h3>
              <div className="space-y-4">
                {integrationsByType.wordpress?.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onRefresh={refetch}
                    variant="detailed"
                  />
                ))}
                {!integrationsByType.wordpress?.length && (
                  <Card>
                    <CardContent className="p-6">
                      <div className="text-center">
                        <h4 className="font-semibold mb-2">WordPress Migration</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Migrate content, media, and SEO data from WordPress
                        </p>
                        <Button onClick={() => handleCreateIntegration('wordpress')}>
                          Setup Migration
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Migration Features</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Content migration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Media transfer
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  URL preservation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  SEO data migration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Database transfer
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Batch processing
                </li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="setup">
          <QuickSetupGuide onCreateIntegration={handleCreateIntegration} />
        </TabsContent>
      </Tabs>

      {/* Create Integration Dialog */}
      <CreateIntegrationDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        selectedType={selectedType}
        onSuccess={() => {
          setShowCreateDialog(false)
          setSelectedType(null)
          refetch()
        }}
      />
    </div>
  )
}
